import {
  FC,
  ReactElement,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { History } from 'history';
import { PRIVATE_COMPETITIONS } from 'utils/constants/sportsbook/private-competitions';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { ResultLine, ResultsGame } from 'interfaces/results';
import { getGameResults } from 'services/sportsbook/results';
import { addQuery } from 'components/QueryRoute';
import { Viewport } from 'components/Viewport';
import { getIsLoggedIn } from 'store/selectors/user-data';
import { GameSingleItemDetailsSkeletonDesktop as DesktopSkeleton } from '../SkeletonDesktop';
import { GameSingleItemDetailsSkeletonMobile as MobileSkeleton } from '../SkeletonMobile';

const { SingleGameMobileView } = lazy(
  () =>
    import(
      /*webpackChunkName: "results-single-game-mobile-view" */ './views/Mobile/index'
    )
);

const { SingleGameDesktopView } = lazy(
  () =>
    import(
      /*webpackChunkName: "results-single-game-desktop-view" */ './views/Desktop/index'
    )
);

type Props = {
  game: ResultsGame;
  isGameOpened?: boolean;
  gameId?: string;
  history: History;
};

export const SingleGame: FC<Props> = ({
  game,
  gameId,
  history
}): ReactElement => {
  const isLoggedIn = useSelector(getIsLoggedIn);

  const [_visible, _setVisible] = useState(gameId === game.game_id);
  const [gameResults, setGameResults] = useState<ResultLine[]>([]);
  const [loading, setLoading] = useState(false);

  const shouldHideGameInfo = useMemo(
    () =>
      !isLoggedIn &&
      PRIVATE_COMPETITIONS[SpringConfigs.JURISDICTION]?.includes(
        game.competition_id
      ),
    [isLoggedIn, game.competition_id]
  );

  const setVisible = useCallback(
    (isVisible: boolean) => {
      _setVisible(isVisible);
      history.push(addQuery({ game_id: isVisible ? game.game_id : undefined }));
    },
    [game.game_id]
  );

  const visible = useMemo(
    () => gameId === game.game_id || _visible,
    [gameId, _visible, game.game_id]
  );

  useLayoutEffect(() => {
    if (visible && !gameResults.length) {
      setLoading(true);
      getGameResults(
        game.game_id,
        (data: { lines: { line: ResultLine[] } }) => {
          setGameResults(data.lines.line);
          setLoading(false);
        }
      );
    }
  }, [visible, gameResults]);

  const groupedLinesByName: Record<string, ResultLine[]>[] = useMemo(() => {
    if (gameResults.length) {
      let counter = 0;
      let half = 0;
      let entries = [];
      const result: Record<string, ResultLine[]> = gameResults.reduce(
        (r, a) => {
          const key = `${counter}${a.line_name}`;
          !r[key] && counter++;
          r[key] = r[key] || [];

          if (shouldHideGameInfo) {
            const lineCopy = { ...a, events: { ...a.events } };
            lineCopy.events.event_name = ['-'];
            r[key].push(lineCopy);
          } else {
            r[key].push(a);
          }

          return r;
        },
        Object.create(null)
      );

      entries = Object.entries(result);
      half = Math.ceil(entries.length / 2);

      return [
        Object.fromEntries(entries.slice(0, half)),
        Object.fromEntries(entries.slice(-half))
      ];
    } else {
      return [{}, {}];
    }
  }, [gameResults, shouldHideGameInfo]);

  return (
    <Viewport
      mobile={SingleGameMobileView}
      desktop={SingleGameDesktopView}
      innerProps={{
        game,
        gameId,
        history,
        visible,
        loading,
        setVisible,
        groupedInfo: groupedLinesByName
      }}
      fallback={
        isMobile() ? (
          <MobileSkeleton />
        ) : (
          <DesktopSkeleton isSingleGameOpened={!!gameId} />
        )
      }
    />
  );
};
